import type {
  CompletedShipmentConfirmation,
  CompletedShipmentInformation,
  ShipmentInformationAddress,
  ShipmentInformationEstimate,
  ShipmentInformationPackaging,
} from '../../../core/api/shipment/models/completed-shipment.model';
import type { EstimatePricing } from '../../../core/models/estimate-rating';
import type { ShipmentFlowState } from '../../../core/services/shipment-flow-state.service';
import {
  computeShipmentSummaryViews,
  type ShipmentSummaryViews,
} from '../../review-and-pay/utils/build-shipment-summary-views';
import type { ReviewAdditionalServicesView } from '../../review-and-pay/utils/map-review-additional-services';
import {
  deduplicateStreetLines,
  formatPhoneNumber,
  type ReviewAddressView,
  type ReviewReturnAddressView,
} from '../../review-and-pay/utils/map-review-address';
import {
  mapReviewInduction,
  type ReviewInductionView,
} from '../../review-and-pay/utils/map-review-induction';
import {
  formatReviewWeight,
  mapSpecialHandlingRowsFromCodes,
  type ReviewPackagingView,
} from '../../review-and-pay/utils/map-review-packaging';
import {
  applyRatedSpecialHandlingPieceCounts,
  countSpecialHandlingPiecesByCode,
} from '../../service-step/sections/delivery-service-selection/build-delivery-service-requests';
import { buildEstimateBreakdown } from '../../service-step/sections/delivery-service-selection/delivery-service-matrix';
import type { SelectedDeliveryService } from '../../service-step/sections/delivery-service-selection/delivery-service-selection.model';

const SIGNATURE_NONE = 'createShipment.reviewAndPay.signature.none';
const SIGNATURE_REQUIRED = 'createShipment.reviewAndPay.signature.required';
const SIGNATURE_ADULT = 'createShipment.reviewAndPay.signature.adult';

/**
 * Formats a numeric shipment amount with two decimals, defaulting non-finite values to '0.00'.
 *
 * @param value Estimate amount from SUCCESS shipmentInformation
 */
function formatShipmentAmount(value: number): string {
  return Number.isFinite(value) ? value.toFixed(2) : '0.00';
}

/**
 * Builds the city / province / postal / country locality line from a Lambda address.
 *
 * @param address Address projection from SUCCESS shipmentInformation
 */
function localityLineFromShipmentAddress(address: ShipmentInformationAddress): string {
  const cityProvince = [address.city.trim(), address.provinceState.trim()]
    .filter((part) => part.length > 0)
    .join(', ');
  const country =
    address.countryCode.trim().toUpperCase() === 'CA' ? 'Canada' : address.countryCode.trim();
  return [cityProvince, address.postalZipCode.trim(), country]
    .filter((part) => part.length > 0)
    .join(' ');
}

/**
 * Normalizes an address fragment to uppercase alphanumerics for duplicate detection.
 *
 * @param value Street or locality fragment
 */
function comparableAddressText(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Detects the Canada Post locality line (`CITY PROV POSTAL`) that AVS can return as an extra
 * street line, which would otherwise repeat the card's own locality line.
 *
 * @param line Street line from SUCCESS shipmentInformation
 * @param address Address projection the line belongs to
 */
function isLocalityStreetLine(line: string, address: ShipmentInformationAddress): boolean {
  const locality = comparableAddressText(
    `${address.city}${address.provinceState}${address.postalZipCode}`,
  );
  return locality.length > 0 && comparableAddressText(line) === locality;
}

/**
 * Maps a Shipment Lambda address projection to the shared Review-style address view.
 * Removes repeated street lines and ignores attention names that repeat a street line. Falls
 * back to wizard name/street/email/phone when the payload is locality-only (older confirmations
 * stored before those fields existed).
 *
 * @param address Address projection from SUCCESS shipmentInformation
 * @param fallback Wizard-mapped address used when Lambda omitted contact lines
 */
function buildShipmentInformationAddressView(
  address: ShipmentInformationAddress,
  fallback: ReviewAddressView,
): ReviewAddressView {
  const companyName = address.companyName?.trim() ?? '';
  const attentionToName = address.attentionToName?.trim() ?? '';
  const attentionMatchesStreet = Boolean(
    attentionToName &&
    [address.streetAddress1, address.streetAddress2, address.streetAddress3].some(
      (line) => line && comparableAddressText(line) === comparableAddressText(attentionToName),
    ),
  );
  const displayAttentionToName = attentionMatchesStreet ? '' : attentionToName;
  const name = (companyName || displayAttentionToName).trim();
  const streetLines = deduplicateStreetLines(
    [address.streetAddress1, address.streetAddress2, address.streetAddress3]
      .map((line) => line?.trim() ?? '')
      .filter((line) => line.length > 0 && !isLocalityStreetLine(line, address)),
  );
  const suiteAptUnit = address.suiteAptUnit?.trim() || fallback.suiteAptUnit;
  const entryBuzzerCode = address.entryBuzzerCode?.trim() || fallback.entryBuzzerCode;
  let contactName = fallback.contactName;
  if (displayAttentionToName && displayAttentionToName !== name) {
    contactName = displayAttentionToName;
  } else if (displayAttentionToName) {
    contactName = '';
  }
  const email = address.email?.trim() ?? '';
  const phone = address.phoneNumber
    ? formatPhoneNumber(address.phoneNumber.trim(), address.extension)
    : '';
  const localityLine = localityLineFromShipmentAddress(address);
  const hasContactFacts = Boolean(
    name ||
    streetLines.length > 0 ||
    suiteAptUnit ||
    entryBuzzerCode ||
    contactName ||
    email ||
    phone,
  );
  if (!hasContactFacts) {
    return {
      ...fallback,
      localityLine: localityLine || fallback.localityLine,
    };
  }

  return {
    name,
    streetLines,
    suiteAptUnit,
    entryBuzzerCode,
    localityLine,
    contactName,
    email,
    phone,
  };
}

/**
 * Builds the Return To Address view from Shipment Lambda details, falling back to
 * "same as Ship From" when the authoritative payload has no distinct return address.
 *
 * @param confirmation Completed-shipment confirmation
 * @param fallback Wizard return-address view
 */
function buildReturnViewFromShipmentInformation(
  confirmation: CompletedShipmentConfirmation | null,
  fallback: ReviewReturnAddressView,
): ReviewReturnAddressView {
  const shipmentInformation = confirmation?.shipmentInformation;
  if (!shipmentInformation) {
    return fallback;
  }

  const returnTo = shipmentInformation.addresses.returnTo;
  if (!returnTo) {
    return { sameAsShipFrom: true, address: null };
  }

  return {
    sameAsShipFrom: false,
    address: buildShipmentInformationAddressView(
      returnTo,
      fallback.address ?? {
        name: '',
        streetLines: [],
        suiteAptUnit: '',
        entryBuzzerCode: '',
        localityLine: '',
        contactName: '',
        email: '',
        phone: '',
      },
    ),
  };
}

/**
 * Maps SUCCESS packaging onto the Packaging review card when weight unit is present.
 *
 * @param packaging Packaging projection from SUCCESS
 * @param fallback Wizard packaging view
 */
function buildPackagingViewFromShipmentInformation(
  packaging: ShipmentInformationPackaging | undefined,
  fallback: ReviewPackagingView | null,
): ReviewPackagingView | null {
  if (!packaging?.weightUnit) {
    return fallback;
  }

  return {
    typeI18nKey: packaging.packageTypeDescription,
    totalPieces: packaging.totalPieces,
    totalWeight: packaging.totalWeight,
    weightDisplay: formatReviewWeight(packaging.totalWeight),
    weightUnit: packaging.weightUnit === 'KG' ? 'kg' : 'lbs',
    // Prefer wizard hierarchy-aware rows when session packaging is still available (OM-702).
    specialHandlingRows:
      fallback?.specialHandlingRows ??
      mapSpecialHandlingRowsFromCodes(
        packaging.specialHandlingTypes ?? [],
        packaging.additionalHandlingCount ?? 0,
      ),
  };
}

/**
 * Maps SUCCESS additional-services onto the Additional Services review card.
 * `packageDescription` is the package type name and is not shown on this card.
 *
 * @param additionalServices Additional-services projection from SUCCESS
 * @param fallback Wizard additional-services view
 */
function buildAdditionalServicesViewFromShipmentInformation(
  additionalServices: CompletedShipmentInformation['additionalServices'],
  fallback: ReviewAdditionalServicesView | null,
): ReviewAdditionalServicesView | null {
  if (!additionalServices) {
    return fallback;
  }

  let signatureI18nKey: string;
  if (additionalServices.signatureType === 'ADULT') {
    signatureI18nKey = SIGNATURE_ADULT;
  } else if (additionalServices.signatureType === 'REQUIRED') {
    signatureI18nKey = SIGNATURE_REQUIRED;
  } else if (additionalServices.signatureType === null) {
    signatureI18nKey = SIGNATURE_NONE;
  } else if (additionalServices.signatureRequired) {
    signatureI18nKey = SIGNATURE_REQUIRED;
  } else {
    signatureI18nKey = SIGNATURE_NONE;
  }

  return {
    declaredValue: additionalServices.declaredValue,
    signatureI18nKey,
  };
}

/**
 * Maps SUCCESS pickupType / pickup onto the induction card.
 * Label-only cannot be expressed by the backend (`DROPOFF` | `PRESCHEDULED`), so the
 * wizard method is kept when the customer chose Create label only.
 * When create soft-fails schedule (PRESCHEDULED without pickupDate), keeps the wizard
 * induction details so Get Labels / print still show the selected day (OM-740).
 *
 * @param shipmentInformation SUCCESS shipmentInformation
 * @param fallback Wizard induction view
 * @param locale Active UI locale
 * @param pickupConfirmationNumber Top-level `CompletedShipmentConfirmation.pickupConfirmationNumber`
 * (OM-731) — already resolved with the `processing.pickup` / alias-key fallbacks in
 * `readPickupConfirmationNumber`; preferred over the nested SUCCESS field, which is used
 * only when the top-level value is unavailable.
 */
function buildInductionViewFromShipmentInformation(
  shipmentInformation: CompletedShipmentInformation,
  fallback: ReviewInductionView | null,
  locale: 'en' | 'fr',
  pickupConfirmationNumber?: string,
): ReviewInductionView | null {
  if (!shipmentInformation.pickupType) {
    return fallback;
  }

  if (shipmentInformation.pickupType === 'DROPOFF') {
    if (fallback?.method === 'label-only') {
      return fallback;
    }

    return mapReviewInduction({ method: 'drop-off' }, locale);
  }

  const pickup = shipmentInformation.pickup;
  if (!pickup?.pickupDate && fallback?.method === 'pickup') {
    const confirmationId =
      pickupConfirmationNumber?.trim() ||
      pickup?.confirmationNumber?.trim() ||
      fallback.pickupConfirmationId;
    return {
      ...fallback,
      pickupConfirmationId: confirmationId?.trim() || null,
    };
  }

  const mapped = mapReviewInduction(
    {
      method: 'pickup',
      selectedDate: pickup?.pickupDate,
      windowFrom: pickup?.readyTime,
      windowTo: pickup?.closeTime,
      locationCode: pickup?.locationCode,
      // SUCCESS pickup block has no courier remarks; keep wizard instructions (OM-830).
      courierInstructions: fallback?.courierInstructions ?? undefined,
    },
    locale,
    pickupConfirmationNumber ?? pickup?.confirmationNumber,
  );

  return {
    ...mapped,
    locationLabel: mapped.locationLabel ?? fallback?.locationLabel ?? null,
  };
}

/**
 * Returns true when SUCCESS estimate already has Additional Surcharges lines to render.
 * All-zero surcharges with an empty accessorial list still count as omitted (OM-735).
 *
 * @param estimate Display estimate from SUCCESS shipmentInformation
 */
function hasSuccessOtherCostLines(estimate: ShipmentInformationEstimate): boolean {
  if ((estimate.accessorialCharges ?? []).length > 0) {
    return true;
  }

  const surcharges = estimate.surcharges;
  return Boolean(
    surcharges && (surcharges.peak > 0 || surcharges.multiPiece > 0 || surcharges.holiday > 0),
  );
}

/**
 * Builds Get Labels pricing from SUCCESS totals, using wizard other-cost lines when the
 * Lambda estimate omitted SH / LDD / Beyond / peak so the card still matches Service.
 *
 * @param estimate SUCCESS display estimate
 * @param wizardPricing Pre-payment selected-service pricing kept in session
 */
function resolveGetLabelsPricing(
  estimate: ShipmentInformationEstimate,
  wizardPricing: EstimatePricing | undefined,
): EstimatePricing {
  const successPricing: EstimatePricing = {
    basePrice: formatShipmentAmount(estimate.shipmentCharge),
    totalPrice: formatShipmentAmount(estimate.totalCharge),
    fuelSurcharge: formatShipmentAmount(estimate.fuelSurchargeAmount),
    taxes: {
      GST: formatShipmentAmount(estimate.gstTaxAmount),
      PST: formatShipmentAmount(estimate.pstTaxAmount),
      HST: formatShipmentAmount(estimate.hstTaxAmount),
      QST: formatShipmentAmount(estimate.qstTaxAmount),
    },
    surcharges: {
      peak: formatShipmentAmount(estimate.surcharges?.peak ?? 0),
      multiPiece: formatShipmentAmount(estimate.surcharges?.multiPiece ?? 0),
      holiday: formatShipmentAmount(estimate.surcharges?.holiday ?? 0),
    },
    ...(estimate.peakSurcharges?.length
      ? {
          peakSurcharges: estimate.peakSurcharges.map((charge) => ({
            productCode: charge.productCode,
            amount: formatShipmentAmount(charge.amount),
          })),
        }
      : {}),
    accessorialCharges: (estimate.accessorialCharges ?? []).map((charge) => ({
      productCode: charge.productCode,
      amount: formatShipmentAmount(charge.amount),
    })),
  };

  if (hasSuccessOtherCostLines(estimate) || !wizardPricing) {
    return successPricing;
  }

  return {
    ...successPricing,
    surcharges: { ...wizardPricing.surcharges },
    ...(wizardPricing.peakSurcharges?.length
      ? { peakSurcharges: [...wizardPricing.peakSurcharges] }
      : {}),
    accessorialCharges: [...wizardPricing.accessorialCharges],
  };
}

/**
 * Resolves special-handling piece counts for Estimated Costs `(Npc)` copy.
 * SUCCESS accessorials are only `{ productCode, amount }`, so Get Labels must keep
 * wizard package counts when the breakdown is rebuilt after create-shipment.
 * SUCCESS `additionalHandlingCount` is used for SH06 only when wizard packages are missing.
 *
 * @param flowState Wizard session that still holds Package-step piece checkboxes
 * @param packaging SUCCESS packaging projection, when the Lambda returned one
 */
function resolveSpecialHandlingPieceCounts(
  flowState: ShipmentFlowState | null,
  packaging: ShipmentInformationPackaging | undefined,
): Record<string, number> | undefined {
  const packageValue = flowState?.package;
  const fromPackage = packageValue
    ? countSpecialHandlingPiecesByCode(packageValue, {
        destinationResidential: flowState.addresses?.shipTo?.residential,
      })
    : undefined;
  const additionalHandlingCount = packaging?.additionalHandlingCount;

  if (!fromPackage && additionalHandlingCount == null) {
    return undefined;
  }

  const counts: Record<string, number> = { ...fromPackage };
  if (typeof additionalHandlingCount === 'number' && counts['SH06'] == null) {
    counts['SH06'] = additionalHandlingCount;
  }

  return counts;
}

/**
 * Builds the selected-service view from Shipment Lambda estimate details for Get Labels,
 * which take precedence over the wizard's pre-payment estimate once a shipment is completed.
 * Badge / commitment lines and backend-rated SH piece counts are not returned by the Lambda,
 * so they are carried from the wizard selection when present. Other-cost lines come from SUCCESS when present, otherwise
 * from the wizard selection so Get Labels / print still show SH, LDD, and Beyond (OM-735).
 *
 * @param confirmation Completed-shipment confirmation
 * @param wizardSelectedService Pre-payment selection used for badge/commitment parity
 */
function buildSelectedServiceFromShipmentInformation(
  confirmation: CompletedShipmentConfirmation | null,
  wizardSelectedService: SelectedDeliveryService | null,
): SelectedDeliveryService | null {
  const shipmentInformation = confirmation?.shipmentInformation;
  if (!shipmentInformation) {
    return null;
  }

  const estimate = shipmentInformation.estimate;
  const displayName =
    shipmentInformation.service?.displayName.trim() || shipmentInformation.productCode;
  return {
    productCode: shipmentInformation.service?.productCode || shipmentInformation.productCode,
    deliveryDate: shipmentInformation.expectedDeliveryDate,
    displayName,
    transportationMode: wizardSelectedService?.transportationMode ?? '',
    guaranteed: wizardSelectedService?.guaranteed,
    badge: wizardSelectedService?.badge,
    commitmentLabel: wizardSelectedService?.commitmentLabel,
    pricing: resolveGetLabelsPricing(estimate, wizardSelectedService?.pricing),
    ratedSpecialHandlingPieceCounts: wizardSelectedService?.ratedSpecialHandlingPieceCounts,
  };
}

/**
 * Builds the address/packaging/pickup/estimate summary views shared by Get Labels and the
 * Shipment Details print view, preferring authoritative Shipment Lambda `shipmentInformation`
 * over the pre-payment wizard state when present.
 *
 * @param flowState Wizard session used as fallback for fields the Lambda cannot express
 * @param confirmation Completed-shipment confirmation from SUCCESS
 * @param translation Active translation service (locale + revision)
 * @param homeLocatorBaseUrl Drop-off locator base URL
 */
export function buildGetLabelsSummaryViews(
  flowState: ShipmentFlowState | null,
  confirmation: CompletedShipmentConfirmation | null,
  translation: { revision: () => unknown; locale: () => string },
  homeLocatorBaseUrl: string,
): ShipmentSummaryViews {
  const views = computeShipmentSummaryViews(flowState, translation, true, homeLocatorBaseUrl);
  const locale = translation.locale() === 'fr' ? 'fr' : 'en';

  const shipmentInformation = confirmation?.shipmentInformation;
  const shipFromView = shipmentInformation
    ? buildShipmentInformationAddressView(
        shipmentInformation.addresses.shipFrom,
        views.shipFromView,
      )
    : views.shipFromView;
  const shipToView = shipmentInformation
    ? buildShipmentInformationAddressView(shipmentInformation.addresses.shipTo, views.shipToView)
    : views.shipToView;
  const returnView = buildReturnViewFromShipmentInformation(confirmation, views.returnView);
  const selectedService =
    buildSelectedServiceFromShipmentInformation(confirmation, views.selectedService) ??
    views.selectedService;
  const packagingView = shipmentInformation
    ? buildPackagingViewFromShipmentInformation(shipmentInformation.packaging, views.packagingView)
    : views.packagingView;
  const additionalServicesView = shipmentInformation
    ? buildAdditionalServicesViewFromShipmentInformation(
        shipmentInformation.additionalServices,
        views.additionalServicesView,
      )
    : views.additionalServicesView;
  const inductionView = shipmentInformation
    ? buildInductionViewFromShipmentInformation(
        shipmentInformation,
        views.inductionView,
        locale,
        confirmation?.pickupConfirmationNumber,
      )
    : views.inductionView;

  if (!selectedService) {
    return {
      ...views,
      shipFromView,
      shipToView,
      returnView,
      packagingView,
      additionalServicesView,
      inductionView,
    };
  }

  return {
    ...views,
    shipFromView,
    shipToView,
    returnView,
    packagingView,
    additionalServicesView,
    inductionView,
    selectedService,
    breakdown: shipmentInformation
      ? buildEstimateBreakdown(selectedService.pricing, {
          specialHandlingPieceCounts: applyRatedSpecialHandlingPieceCounts(
            resolveSpecialHandlingPieceCounts(flowState, shipmentInformation.packaging),
            selectedService.ratedSpecialHandlingPieceCounts,
          ),
          discountedPricing: selectedService.discountedPricing,
          discountRate: selectedService.couponDiscount?.discountRate,
        })
      : views.breakdown,
  };
}
